import {
  Autocomplete,
  FormControl,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  SxProps,
  TableCell,
  TableCellProps,
  TextField,
  Theme,
} from "@mui/material"
import {
  MouseEvent as ReactMouseEvent,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import { enqueueSnackbar } from "notistack"
import { Control, Controller, FieldValues, Path } from "react-hook-form"
import { Delete } from "@mui/icons-material"
import { CUSTOM_ERROR_ATTRIBUTE } from "@utils/form"

export enum EditType {
  Text = "text",
  Color = "color",
  Number = "number",
  NumberOrSingleOption = "number-or-single-option",
  SingleOption = "single-option",
}

export type EditableCellProps<TFieldValues extends FieldValues = FieldValues> = Omit<
  TableCellProps,
  "onBlur" | "onChange"
> & {
  onRemove?: (value: EditableCellProps["value"], name: EditableCellProps["name"]) => void
  onChange?: (newValue: unknown) => void
  editable?: boolean
  markNonEditable?: boolean
  options?: { label: string; value: string }[]
  children?: ReactNode
  value: string | number | null
  onBlur?: (newValue: string) => void
  type?: EditType
  editOnClick?: boolean
  control?: Control<TFieldValues>
  name?: Path<TFieldValues>
  errorMessage?: string
}

const EditableCell = <TFieldValues extends FieldValues>({
  editable = false,
  children,
  value,
  markNonEditable = false,
  options = [],
  type = EditType.Text,
  onBlur,
  sx,
  editOnClick = true,
  control,
  errorMessage = "",
  name,
  onRemove,
  ...props
}: EditableCellProps<TFieldValues>) => {
  const [isEditing, setIsEditing] = useState(!editOnClick)
  const inputRef = useRef<HTMLInputElement>()

  useLayoutEffect(() => {
    if (isEditing && editOnClick) inputRef.current?.focus()
  }, [editOnClick, isEditing])

  useEffect(() => {
    if (!inputRef.current || !value) return
    inputRef.current.value = value.toString()
  }, [value])

  const handleRemove = (event: ReactMouseEvent) => {
    event.stopPropagation()

    onRemove?.(value, name)
  }

  const removeButton = onRemove && (
    <IconButton sx={{ mr: 1 }} size="small" onClick={handleRemove}>
      <Delete />
    </IconButton>
  )

  if (!editable) {
    return (
      <TableCell
        {...props}
        sx={{
          ...(markNonEditable ? { backgroundColor: "rgba(225, 225, 225, 0.3)" } : {}),
          ...sx,
        }}
      >
        {removeButton}
        {children || value}
      </TableCell>
    )
  }

  if (!isEditing) {
    return (
      <TableCell
        sx={{
          cursor: "pointer",
          "&:hover": { backgroundColor: "rgba(225, 225, 225, 0.4)" },
          ...sx,
        }}
        onClick={() => setIsEditing(true)}
        {...props}
      >
        {removeButton}
        {children || value}
        <FormHelperText
          error
          sx={{ mt: 0 }}
          {...{ [CUSTOM_ERROR_ATTRIBUTE]: Boolean(errorMessage) }}
        >
          {errorMessage}
        </FormHelperText>
      </TableCell>
    )
  }

  const handleBlur = (value?: string) => {
    setIsEditing(false)
    onBlur?.(value ?? inputRef.current?.value ?? "")
  }

  const handleNumberOrSingleOptionBlur = () => {
    const isNumber = !isNaN(Number(inputRef.current?.value))
    const selectedOption = options.find(({ label }) => label === inputRef.current?.value)

    if (!isNumber && !selectedOption) {
      setIsEditing(false)
      enqueueSnackbar("Cell value have to be number or parent node", { variant: "error" })
      return
    }

    const value = selectedOption ? selectedOption.value : inputRef.current?.value ?? ""
    setIsEditing(false)
    onBlur?.(value)
  }

  if (type === EditType.NumberOrSingleOption) {
    return (
      <TableCell sx={sx}>
        {removeButton}
        {control && name ? (
          <Controller
            control={control}
            name={name}
            render={({ field }) => (
              <Autocomplete<{ label: string; value: string } | number, false, false, true>
                freeSolo
                options={options}
                defaultValue={value !== null ? value.toString() : ""}
                size="small"
                fullWidth
                getOptionLabel={(option) => {
                  if (typeof option === "string") return option
                  if (typeof option === "number") return option.toString()
                  return option.label
                }}
                renderInput={(params) => (
                  <TextField
                    error={!!errorMessage}
                    helperText={errorMessage}
                    name={field.name}
                    onChange={(event) => {
                      props.onChange?.(event.target.value)
                      field.onChange(event.target.value)
                    }}
                    {...params}
                    inputRef={inputRef}
                  />
                )}
                componentsProps={{ popper: { style: { width: "fit-content" } } }}
                {...field}
                value={options.find((option) => option.value === field.value)?.label ?? field.value}
                onChange={(_event, value) => {
                  let newValue = value

                  if (typeof value === "string" || value === null) newValue = value
                  else if (typeof value === "number") newValue = value.toString()
                  else newValue = value.value

                  props.onChange?.(newValue)
                  field.onChange(newValue)
                }}
                onBlur={editOnClick ? handleNumberOrSingleOptionBlur : undefined}
              />
            )}
          />
        ) : (
          <Autocomplete<{ label: string; value: string }, false, false, true>
            open
            freeSolo
            options={options}
            defaultValue={value !== null ? value.toString() : ""}
            size="small"
            fullWidth
            onBlur={editOnClick ? handleNumberOrSingleOptionBlur : undefined}
            getOptionLabel={(option: string | number | { label: string; value: string }) => {
              return typeof option === "string" || typeof option === "number"
                ? option.toString()
                : option.label
            }}
            renderInput={(params) => (
              <TextField
                error={!!errorMessage}
                helperText={errorMessage}
                {...params}
                onChange={(e) => props.onChange?.(e.target.value)}
                inputRef={inputRef}
              />
            )}
            componentsProps={{ popper: { style: { width: "fit-content" } } }}
          />
        )}
      </TableCell>
    )
  }

  if (type === EditType.SingleOption) {
    return (
      <TableCell>
        {removeButton}
        {control && name ? (
          <Controller
            control={control}
            name={name}
            render={({ field }) => (
              <FormControl error={!!errorMessage} fullWidth>
                <Select
                  {...field}
                  onBlur={(e) => onBlur?.(e.target.value)}
                  onChange={(e) => {
                    props.onChange?.(e.target.value)
                    field.onChange(e.target.value)
                  }}
                  size="small"
                  fullWidth
                  inputRef={inputRef}
                >
                  {options.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {errorMessage && (
                  <FormHelperText error {...{ [CUSTOM_ERROR_ATTRIBUTE]: true }}>
                    {errorMessage}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />
        ) : (
          <FormControl error={!!errorMessage} fullWidth>
            <Select
              value={value}
              onBlur={(e) => onBlur?.(e.target.value)}
              onChange={(e) => props.onChange?.(e.target.value)}
              size="small"
              fullWidth
              inputRef={inputRef}
            >
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {errorMessage && (
              <FormHelperText error {...{ [CUSTOM_ERROR_ATTRIBUTE]: true }}>
                {errorMessage}
              </FormHelperText>
            )}
          </FormControl>
        )}
      </TableCell>
    )
  }

  return (
    <TableCell sx={sx}>
      {control && name ? (
        <Controller
          control={control}
          name={name}
          render={({ field }) => (
            <TextField
              {...field}
              error={!!errorMessage}
              helperText={errorMessage}
              size="small"
              fullWidth
              value={field.value ?? ""}
              onBlur={({ target: { value } }) => (editOnClick ? handleBlur(value) : undefined)}
              onChange={(e) => {
                props.onChange?.(e.target.value)
                field.onChange(e.target.value)
              }}
              type={type === EditType.Number ? "number" : "text"}
              sx={sxCell}
              inputRef={inputRef}
            />
          )}
        />
      ) : (
        <TextField
          error={!!errorMessage}
          helperText={errorMessage}
          size="small"
          fullWidth
          defaultValue={value}
          value={inputRef.current?.value}
          onBlur={() => (editOnClick ? handleBlur() : undefined)}
          onChange={(e) => props.onChange?.(e.target.value)}
          inputRef={inputRef}
          type={type === EditType.Number ? "number" : "text"}
          sx={sxCell}
        />
      )}
    </TableCell>
  )
}

const sxCell: SxProps<Theme> = {
  // Hide number input arrows in Chromium browsers
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
    {
      WebkitAppearance: "none",
      margin: 0,
    },
  // Hide number input arrows in Firefox
  "& input[type=number]": {
    MozAppearance: "textfield",
  },
  "& fieldset": {
    border: "none",
  },
  "& input": {
    paddingLeft: ".3em",
    paddingRight: ".3em",

    // show ellipsis when too much text
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
    minWidth: "1.1em", // Minimum width for one digit and ellipsis
  },
  display: "flex",
  justifyContent: "center",
}

export default EditableCell
